/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Box } from '@mui/material';
import { Formik, FormikProps } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DESTINATION_TYPES } from '../../../containers/destination/constants/destinationTypes';
import { GlobalConfigAPI } from '../../../core/GlobalConfigAPI';
import { GlobalConfigService } from '../../../core/services/global-config.service';
import useService from '../../../injector/hooks/useService';
import { FormState } from '../interfaces/form';
import { BigQueryPartitionTypeEnum } from '../interfaces/interface';
import { SchemaBuilderForm } from './schemaBuilderForm';
import { NESTED_DATA_TYPES } from '../../../core/constants/constants';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import {
  BIGQUERY_PARTITION_STYLES,
  DATA_TYPE_JSON,
  DATA_TYPE_JSON_STORED_VALUE, DEFAULT_SCHEMA_BUILDER_OPTIONS
} from '../constants/constants';
import DestinationAPI from '../../../containers/destination/api/DestinationsAPI';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import ErrorFocus from '../../../form/components/error-focus/ErrorFocus';
import { useConfirm } from '../../../modal/components/confirm-dialog-v1/ConfirmDialogV1Provider';
import { TRACKER_CANCEL_CLICK, TRACKER_CREATE_TABLE_CLICK } from '../tracking/schemaBuilderTracking';
import { validationSchema } from '../validators/validateSourceType';
import { DestinationTypesAPI } from '../api/DestinationTypesAPI';
import { MapperService } from '../api/MapperService';
import { generateMappingForStruct, generateSchemaRequestFields } from '../utils/schemaBuilder';
import { CREATE_OPTION_ID } from '../../../dropdown/components/creatable-option-dropdown/CreatableOptionDropdown';

const processDataTypes = (dataTypes, removeNestedDataTypes) => {
  if (removeNestedDataTypes) {
    return dataTypes.filter(type => !NESTED_DATA_TYPES.includes(type.type));
  }

  return dataTypes;
};

export interface SchemaBuilderProps {
  fields?: any[];
  destinationId: number;
  integrationId?: number;
  dataTypes?: any[];
  schemaName?: string;
  tableName?: string;
  sourceSchemaName?: string;
  close: (data?: any) => void;
  options?: {
    addNewFields?: boolean;
    destType?: string;
    withMappingData?: boolean;
    mappingId?: number;
    removeNestedDataTypes?: boolean;
  };
}

export default function SchemaBuilder({
  dataTypes,
  destinationId,
  fields,
  options: passedOptions,
  integrationId,
  schemaName,
  close
}: SchemaBuilderProps) {
  const formikRef = useRef<FormikProps<any>>();

  const [dataTypesLoading, setDataTypesLoading] = useState(false);
  const [destDataTypes, setDestDataTypes] = useState(
    processDataTypes(dataTypes, passedOptions.removeNestedDataTypes)
  );
  const [tableNameLengthLimit, setTableNameLengthLimit] = useState(0);
  const { confirm } = useConfirm();
  const [destinationTables, setDestinationTables] = useState([]);
  const [mappingToCreatedTable, setMappingToCreatedTable] = useState(false);
  const [error, setError] = useState(null);
  const [creatingTable, setCreatingTable] = useState(false);
  const { eventTrack } = useAnalyticsTracker();

  const options: {
    addNewFields?: boolean;
    destType?: string;
    withMappingData?: boolean;
    mappingId?: number;
    removeNestedDataTypes?: boolean;
  } = {
    ...DEFAULT_SCHEMA_BUILDER_OPTIONS,
    ...passedOptions
  };

  const isFireboltDest = options.destType === DESTINATION_TYPES.FIREBOLT.name;
  const isBQDest = options.destType === DESTINATION_TYPES.BIGQUERY.name;
  const isDataBricks = options.destType === DESTINATION_TYPES.DATABRICKS.name;
  const isRedshift = options.destType === DESTINATION_TYPES.REDSHIFT.name;
  const isSnowFlake = options.destType === DESTINATION_TYPES.SNOWFLAKE.name;
  const globalConfigService = useService(GlobalConfigService);

  useEffect(() => {
    formikRef.current.validateForm();
  }, [tableNameLengthLimit]);

  useEffect(() => {
    Promise.all([
      GlobalConfigAPI.getConfig('destination_identifier_length_limits'),
      DestinationTypesAPI.getDataTypes(options.destType, false)
    ]).then(([validations, res]) => {
      setTableNameLengthLimit(validations[options.destType].max_table_name_length);
      setDestDataTypes(processDataTypes(res.data, options.removeNestedDataTypes));
      setDataTypesLoading(false);
    });

    DestinationAPI.getTables(destinationId).then(res => {
      setDestinationTables(res.data.map(({ name }) => name));
    });
  }, [destinationId, globalConfigService, options.destType, options.removeNestedDataTypes]);

  const getDestinationFieldsPayload = (values: FormState) =>
    values.fields
      .filter(
        field => !field.inclusionItem || (field.inclusionItem && field.inclusionItem.included)
      )
      .map((field: any) => {
        let dataType;

        if (field.dataType.id === CREATE_OPTION_ID) {
          dataType = field.dataType.value;
        } else {
          dataType = field.dataType.type_display || field.dataType;
        }

        if (dataType === DATA_TYPE_JSON) {
          dataType = DATA_TYPE_JSON_STORED_VALUE;
        }

        const temp: any = {
          name: field.destination.name,
          primary: field.isPrimaryKey,
          type: {
            type: dataType
          }
        };

        if (isFireboltDest || isDataBricks) {
          temp.partition = field.isPartitionKey;
        }

        if (isFireboltDest) {
          temp.optional = field.optional;
          temp.sort = field.isSortKey;
        }

        if (isBQDest || isSnowFlake) {
          temp.cluster = field.isClusterKey;
        }

        if (isRedshift) {
          temp.sort = field.isSortKey;
          if (values.distKey?.uniqId === field.uniqId) {
            temp.dist = true;
          }
        }

        if (isBQDest && values.partitionKey?.uniqId === field.uniqId) {
          temp.partition = true;
        }

        if (temp.type.type.toLowerCase() === 'array') {
          temp.value_type = {
            type: field.destination.value_type.type_display
          };
        }

        if (field.nested) {
          temp.fields = generateSchemaRequestFields(field.nested, 'destination');
        }

        return temp;
      });

  /**
   * Get the DTO for all the fields that
   * will be consumed by the services
   */
  const getFieldsMappingDTO = () =>
    formikRef.current.values.fields.map(field => {
      const fieldMapping: any = {
        sf: field.source.name,
        sft: field.source.type.type_display,
        ig: !field.inclusionItem.included,
        ty: field.source.meta_type,
        df: field.destination.name
      };

      let dataType;

      if (field.dataType.id === CREATE_OPTION_ID) {
        dataType = field.dataType.value;
      } else {
        dataType = field.dataType.type_display || field.dataType;
      }

      if (dataType === DATA_TYPE_JSON) {
        dataType = DATA_TYPE_JSON_STORED_VALUE;
      }

      fieldMapping.dft = dataType;

      if (field.nested) {
        fieldMapping.ns = generateMappingForStruct(field.nested);
      }

      return fieldMapping;
    });

  const mapCreatedTable = destTableName => {
    const params = {
      destination_schema_name: destTableName,
      destination_id: destinationId,
      mappings: getFieldsMappingDTO()
    };

    MapperService.updateMapping(integrationId, options.mappingId, params).then(
      () => {
        setMappingToCreatedTable(false);

        close({
          tableName: destTableName
        });
      },
      err => {
        setMappingToCreatedTable(false);
        setError(getErrorMessageFromObj(err));
      }
    );
  };

  const getMissingPKWarning = (values: FormState) => {
    const isPrimaryKeySelected: boolean = values.fields.some(
      (field: any) =>
        field.isPrimaryKey &&
        ((options.withMappingData && field.inclusionItem.included) || !options.withMappingData)
    );

    if (isPrimaryKeySelected) {
      return undefined;
    }

    return 'Primary key is not defined for this table. This may result in data duplication. Are you sure?';
  };

  const createTable = (values: FormState) => {
    eventTrack({
      action: TRACKER_CREATE_TABLE_CLICK
    });

    setError(null);
    setCreatingTable(true);

    const parsedFields = getDestinationFieldsPayload(values);
    MapperService.getCreateTableQuery(
      integrationId,
      destinationId,
      values.tableName,
      parsedFields,
      values.partitionStyle,
      values.partitionType,
      values.fireboltTableType?.tableType
    )
      .then(() => {
        if (!isFireboltDest) {
          const pkWarning = getMissingPKWarning(values);
          if (pkWarning) {
            return confirm({
              title: 'Create Table',
              body: pkWarning,
              positiveButtonText: 'Yes',
              negativeButtonText: 'No'
            });
          }
        }
        return Promise.resolve(true);
      })
      .then(res => {
        if (!res) {
          return Promise.resolve(null);
        }

        return MapperService.createTable(
          integrationId,
          destinationId,
          values.tableName,
          parsedFields,
          values.partitionStyle,
          values.partitionType,
          values.fireboltTableType?.tableType,
          true
        );
      })
      .then(res => {
        setCreatingTable(false);

        if (!res) {
          return;
        }

        if (options.withMappingData) {
          setMappingToCreatedTable(true);
          mapCreatedTable(res?.data?.schema_name);
          return;
        }

        close({
          destinationId,
          tableName: res.data.schema_name,
          fields: values.fields
        });
      })
      .catch(err => {
        setError(err);
        setCreatingTable(false);
      });
  };

  const handleSubmit = values => {
    createTable(values);
  };

  const handleCancelClick = () => {
    eventTrack({
      action: TRACKER_CANCEL_CLICK
    });

    close();
  };

  const defaultData: FormState = {
    tableName: schemaName,
    fields: [],
    partitionStyle: BIGQUERY_PARTITION_STYLES[0],
    partitionKey: null,
    partitionType: BigQueryPartitionTypeEnum.DAY
  };

  const schemaBuilderValidationSchema = useMemo(
    () =>
      validationSchema(
        options.destType,
        tableNameLengthLimit,
        destinationTables,
        options.withMappingData
      ),
    [destinationTables, options.destType, options.withMappingData, tableNameLengthLimit]
  );

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={schemaBuilderValidationSchema}
      initialTouched={{
        tableName: true
      }}
      initialValues={defaultData}
      onSubmit={handleSubmit}
    >
      {formikProps => (
        <Box component='form' className='' onSubmit={formikProps.handleSubmit} noValidate>
          <ErrorFocus formik={formikProps} onFormError={() => {}} />

          <SchemaBuilderForm
            creatingTable={creatingTable}
            error={error}
            handleCancelClick={handleCancelClick}
            initialFields={fields || []}
            tableNameLengthLimit={tableNameLengthLimit}
            mappingToCreatedTable={mappingToCreatedTable}
            hasErrors={Object.keys(formikProps.errors).length > 0}
            formikRef={formikRef}
            dataTypes={destDataTypes}
            dataTypesLoading={dataTypesLoading}
            options={options}
          />
        </Box>
      )}
    </Formik>
  );
}
